import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { AutoCompleteDropdownComponent } from '../../../shared/controls/auto-complete-dropdown/auto-complete-dropdown.component';

import { ErrorService } from '../../../shared/services/error-service';
import { WorkbookUploadService, UploadProgressEvent } from '../../../shared/services/workbook-upload.service';

import { AgeingService, PeriodService, ClientService, AllPeriodItem } from '../../../../service/proxy';
import { ToastService } from '../../../shared/toast/toast.service';

@Component({
    selector: 'app-create-ageing-batch',
    templateUrl: './create-ageing-batch.component.html',
    styleUrls: ['./create-ageing-batch.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
        AutoCompleteDropdownComponent,
    ],

    providers: [
        AgeingService,
        ClientService,
        PeriodService,
    ]
})
export class CreateAgeingBatchComponent implements OnInit {
    public batchId: number = 0;
    public isSaving: boolean = false;
    public uploadProgress: number = 0;
    public uploadMessage: string = '';

    public selectedPeriodId: number | undefined = undefined;
    public periods: AllPeriodItem[] = [];
    public formSubmitted: boolean = false;

    public selectedClientId: number | undefined = undefined;
    public clients: { id: number; name: string }[] = [];

    public selectedFile: File | null = null;
    public selectedFileName: string = '';

    constructor(
        private router: Router,
        private ageingService: AgeingService,
        private periodService: PeriodService,
        private clientService: ClientService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private cdr: ChangeDetectorRef,
        private workbookUploadService: WorkbookUploadService) {}

    ngOnInit(): void {
        this.getPeriods();
        this.getClients();
    }

    getPeriods(): void {
        this.periodService.getAllPeriods()
            .subscribe((data) => {
                this.periods = data.results || [];
                this.selectedPeriodId = this.periods[1]?.id ?? 0;
            });
    }

    getClients(): void {
        this.clientService.getAllClients()
            .subscribe({
                next: (response) => {
                    this.clients = response.results.map(client => ({
                        id: client.id,
                        name: client.clientName
                    }));
                },
                error: (error: any) => {
                    this.errorService.handleError(error);
                }
            }
        );
    }

    onPeriodSelectionChange(selectedId: number | null): void {
        this.selectedPeriodId = selectedId ?? undefined;
    }

    onClientSelectionChange(selectedId: number | null): void {
        this.selectedClientId = selectedId ?? undefined;
    }

    displayPeriod(option: any): string {
        const paddedMonth = ('0' + option.month).slice(-2);
        return `${option.year}-${paddedMonth}`;
    }

    onFileSelected(event: any): void {
        const input = event.target as HTMLInputElement;

        if (input.files && input.files.length > 0) {
            this.selectedFile = input.files[0];
            this.selectedFileName = input.files[0].name;
        }
        else {
            this.selectedFile = null;
            this.selectedFileName = '';
        }
    }

    onSave(form: any): void {
        this.formSubmitted = true;

        if (form.invalid || !this.selectedFile || !this.selectedPeriodId) {
            this.isSaving = false;
            return;
        }

        this.isSaving = true;
        this.uploadProgress = 0;
        this.uploadMessage = 'Preparing upload...';

        this.workbookUploadService.upload({
            importer: 'ageing',
            file: this.selectedFile,
            fields: {
                periodId: this.selectedPeriodId,
                clientId: this.selectedClientId,
            }
        }).subscribe({
            next: (event: UploadProgressEvent) => {
                this.uploadProgress = event.progress;
                this.uploadMessage = event.message;
                this.cdr.detectChanges();

                if (event.progress === 100) {
                    if (event.isExisting) {
                        this.toastService.show('Cannot upload — a pending import exists for this client and period. Delete it first.', 'info');
                        this.isSaving = false;
                        return;
                    }

                    sessionStorage.setItem('isRedirected', true.toString());
                    this.router.navigate([`/ageing-wizard/${event.batchId}`], {
                        queryParams: { clientId: this.selectedClientId }
                    });
                }
            },
            error: (error: Error) => {
                if (error.message?.includes('xlsx')) {
                    this.toastService.show('Invalid file extension. Only .xlsx files are allowed.', 'warning');
                }
                else if (error.message?.includes('400')) {
                    this.toastService.show('Bad request. Please check the file and try again.', 'info');
                }
                else {
                    this.toastService.show(error.message || 'Upload failed. Please try again.', 'error');
                }

                this.isSaving = false;
                this.uploadProgress = 0;
                this.uploadMessage = '';
            }
        });
    }

    onCancel(): void {
        this.router.navigate([`/ageing`]);
    }
}
